import { FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useSendWebhookTestEventMutation } from "@/pages/Private/redux/webhook/webhook.api";
import { getAllErrors, renderErrorMessages } from "@/utils";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { Webhook } from "../../schema/webhook";
import { WebhookTestEventResult } from "../../schema/webhookSigning";

interface WebhookTestEventDialogProps {
	webhook: Pick<Webhook, "id" | "name" | "url">;
	onClose: () => void;
	onRotate: () => void;
}

/**
 * "Send test event" (PREC-4249): sends one signed sample on open and shows what the receiver
 * answered and exactly what was sent. Mounted per webhook (keyed by its id), so an answer is
 * always for the webhook the dialog names (web §23).
 */
export const WebhookTestEventDialog: FunctionComponent<WebhookTestEventDialogProps> = ({
	webhook,
	onClose,
	onRotate,
}) => {
	const { t } = useTranslation();
	const [sendTestEvent, { data: result, error, isLoading, reset }] =
		useSendWebhookTestEventMutation();
	const { run, isRunning } = useSingleFlight();

	const send = () =>
		run(async () => {
			reset();
			// A refusal (429 too soon, 404) is shown in the dialog from the mutation's `error`.
			await sendTestEvent(webhook.id)
				.unwrap()
				.catch(() => undefined);
		});

	useEffect(() => {
		// Sent once on open; "Send again" sends the next one. A second mount effect (StrictMode) is
		// dropped by the single flight while the first is running.
		send();
	}, []);

	const busy = isRunning || isLoading;

	return (
		<Modal
			footer={
				<div className="flex justify-end gap-2">
					{result && !result.delivered && (
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.S}
							testId="webhook-test-rotate"
							title={t("webhookSigning.menu.rotateSecret")}
							type="button"
							onClick={onRotate}
						/>
					)}
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={busy}
						isLoading={busy}
						size={ButtonSize.S}
						testId="webhook-test-send-again"
						title={t("webhookSigning.test.sendAgain")}
						type="button"
						onClick={send}
					/>
					<Button
						color={ButtonColor.ACTION}
						size={ButtonSize.S}
						testId="webhook-test-close"
						title={t("webhookSigning.test.close")}
						type="button"
						onClick={onClose}
					/>
				</div>
			}
			handleClose={onClose}
			isOpened={true}
			size="md"
			title={t("webhookSigning.test.title", { name: webhook.name })}
		>
			<div className="flex flex-col gap-3 text-sm text-text" data-test="webhook-test-dialog">
				<p>{t("webhookSigning.test.lead", { url: webhook.url })}</p>
				{busy && <p className="text-text-meta">{t("webhookSigning.test.sending")}</p>}
				{!busy && result && <WebhookTestEventOutcome result={result} />}
				{!busy && error ? renderErrorMessages(getAllErrors(error, [])) : null}
				<p className="text-xs text-text-meta">{t("webhookSigning.test.note")}</p>
			</div>
		</Modal>
	);
};

/** The receiver's answer, then what was sent (delivered) or what it said (not delivered). */
const WebhookTestEventOutcome: FunctionComponent<{ result: WebhookTestEventResult }> = ({
	result,
}) => {
	const { t } = useTranslation();
	const params = { outcome: result.outcome, ms: String(result.durationMs) };
	let detailKey = "webhookSigning.test.notDeliveredNoAnswer";

	if (result.delivered) {
		detailKey = "webhookSigning.test.deliveredDetail";
	} else if (result.status !== null) {
		detailKey = "webhookSigning.test.notDeliveredAnswer";
	}

	return (
		<div className="flex flex-col gap-2" data-test="webhook-test-outcome">
			<p>
				<span
					className={
						result.delivered ? "font-medium text-status-success" : "font-medium text-status-error"
					}
				>
					{t(
						result.delivered ? "webhookSigning.test.delivered" : "webhookSigning.test.notDelivered"
					)}
				</span>{" "}
				{t(detailKey, params)}
			</p>
			{result.delivered ? (
				<WebhookTestEventBlock
					lines={[
						`POST ${result.sent.url}`,
						...Object.entries(result.sent.headers)
							.filter(([name]) => name.startsWith("X-Signalist"))
							.map(([name, value]) => `${name}: ${value}`),
						"",
						result.sent.body,
					]}
					title={t("webhookSigning.test.whatWasSent")}
				/>
			) : (
				result.status !== null && (
					<WebhookTestEventBlock
						lines={[
							`HTTP ${result.outcome}`,
							...(result.responseBody ? [result.responseBody] : []),
						]}
						title={t("webhookSigning.test.receiversAnswer")}
					/>
				)
			)}
		</div>
	);
};

const WebhookTestEventBlock: FunctionComponent<{ title: string; lines: string[] }> = ({
	title,
	lines,
}) => (
	<div className="text-xs">
		<div className="mb-1 font-medium">{title}</div>
		<pre className="whitespace-pre-wrap break-all rounded-md bg-background-light p-2 font-mono text-[12px]">
			{lines.join("\n")}
		</pre>
	</div>
);
